import { useEffect, useRef } from 'react'

import { EMPTY_OBJECT } from '../../../../constants/common'
import {
  getHasGretaAIChatUnread,
  getIsGretaAIChatChatRunning,
} from '../../GretaAIChat.utils'
import { getIsGretaAIChatProjectsEnabled } from '../../GretaAIChat.utils.projects'
import { useGretaAIChatBalance } from '../useGretaAIChatBalance'
import { useGretaAIChatChatActions } from '../useGretaAIChatChatActions'
import { useGretaAIChatChatListLoader } from '../useGretaAIChatChatListLoader'
import { useGretaAIChatConversation } from '../useGretaAIChatConversation'
import { useGretaAIChatDrafts } from '../useGretaAIChatDrafts'
import { useGretaAIChatFeedback } from '../useGretaAIChatFeedback'
import { useGretaAIChatFocus } from '../useGretaAIChatFocus'
import { useGretaAIChatPanelState } from '../useGretaAIChatPanelState'
import { useGretaAIChatProjects } from '../useGretaAIChatProjects'
import { useGretaAIChatRowStatus } from '../useGretaAIChatRowStatus'

import { useGretaAIChatSubmit } from './useGretaAIChatSubmit'
import { useGretaAIChatUnreadReplies } from './useGretaAIChatUnreadReplies'

export function useGretaAIChatSession({ displayName } = EMPTY_OBJECT) {
  const launcherRef = useRef(null)
  const panelRef = useRef(null)
  const chatListScrollTopRef = useRef(0)
  const chatListSeenChatIdRef = useRef(null)

  const {
    activeChatId,
    chatListWidth,
    closeChatList,
    handleMinimize,
    handleOpen,
    handleToggleMode,
    hoverChatList,
    isChatListColumnOpen,
    isChatListHovered,
    isChatListOrderFrozen,
    isChatListPinned,
    isMinimized,
    isOpen,
    mode,
    openChat,
    openNewChat,
    openProjectsPage,
    pinChatList,
    setActiveChatId,
    setChatListWidth,
    setIsChatListOrderFrozen,
    toggleChatListColumn,
    unhoverChatList,
    view,
  } = useGretaAIChatPanelState()

  const balance = useGretaAIChatBalance()
  const { checkBalance, isLimitReached, isLocked } = balance

  const activeChatIdRef = useRef(activeChatId)

  useEffect(() => {
    activeChatIdRef.current = activeChatId
  }, [activeChatId])

  useEffect(() => {
    checkBalance()
  }, [])

  const chatListLoader = useGretaAIChatChatListLoader({
    activeChatId,
    isMinimized,
    isOpen: isOpen && !isLocked,
    onActiveChatMissing: handleChatMissing,
  })
  const {
    addChat,
    chats,
    dropChat,
    getIsChatGenerationSettledRecently,
    refreshChats,
    settleChatGeneration,
  } = chatListLoader

  const {
    chatStatusById,
    clearChatUnread,
    markChatGenerating,
    markChatSettled,
  } = useGretaAIChatRowStatus({ chats })

  const projects = useGretaAIChatProjects({
    chats,
    isOpen: getIsGretaAIChatProjectsEnabled() && isOpen && !isLocked,
  })

  function handleChatCreated({ chatData, chatId, shouldActivate }) {
    if (shouldActivate) {
      setActiveChatId(chatId)
    }

    addChat(chatData)
    refreshChats()
  }

  // Clear only if the probed/loaded chat is still the open one — a slow probe must
  // not drop a selection the user has since moved to another chat.
  function handleChatMissing(chatId) {
    if (chatId === undefined || chatId === activeChatIdRef.current) {
      setActiveChatId(null)
    }

    if (chatId !== undefined) {
      dropChat(chatId)
    }
  }

  function handleSelectChat(chatId) {
    clearChatUnread(chatId)
    openChat(chatId)
  }

  const { handleOpenPanel, handleTurnSettled } = useGretaAIChatUnreadReplies({
    activeChatId,
    checkBalance,
    clearChatUnread,
    handleOpen,
    isMinimized,
    isOpen,
    markChatSettled,
    view,
  })

  const {
    forgetChat,
    isReplySettling,
    loadOlderMessages,
    messages,
    pendingAnswerFileName,
    reloadMessages,
    retryLastReply,
    sendMessage,
    status,
    stopGeneration,
    trackBackgroundGeneration,
    turnStartedAt,
  } = useGretaAIChatConversation({
    activeChatId,
    getIsGenerationSettledRecently: getIsChatGenerationSettledRecently,
    onChatCreated: handleChatCreated,
    onChatMissing: handleChatMissing,
    onGenerationSettled: settleChatGeneration,
    onTurnSettled: handleTurnSettled,
    onTurnStart: markChatGenerating,
  })

  useEffect(() => {
    chats.forEach(chat => {
      if (getIsGretaAIChatChatRunning(chat)) {
        trackBackgroundGeneration(chat.id)
      }
    })
  }, [chats, trackBackgroundGeneration])

  const { discardChatDraft, discardDraftIfSubmitted, draft, setDraft } =
    useGretaAIChatDrafts(activeChatId)

  const {
    chatAction,
    closeChatAction,
    confirmDeleteChat,
    confirmRenameChat,
    openDeleteChat,
    openRenameChat,
  } = useGretaAIChatChatActions({
    activeChatId,
    chatListLoader,
    discardChatDraft,
    forgetChat,
    setActiveChatId,
  })

  const { feedbackByMessageId, submitFeedback } = useGretaAIChatFeedback({
    activeChatId,
    messages,
  })

  useGretaAIChatFocus({
    isMinimized,
    isOpen,
    launcherRef,
    panelRef,
  })

  const { handleRetry, handleSendMessage, isSendPending } =
    useGretaAIChatSubmit({
      activeChatIdRef,
      balance,
      discardDraftIfSubmitted,
      retryLastReply,
      sendMessage,
    })

  return {
    actions: {
      setActiveChatId,
      setDraft,
      onChatListResize: setChatListWidth,
      onCloseChatAction: closeChatAction,
      onCloseChatList: closeChatList,
      onConfirmDeleteChat: confirmDeleteChat,
      onConfirmRenameChat: confirmRenameChat,
      onDeleteChat: openDeleteChat,
      onHoverChatList: hoverChatList,
      onMinimize: handleMinimize,
      onNewChat: openNewChat,
      onOpenProjectsPage: openProjectsPage,
      onPinChatList: pinChatList,
      onRenameChat: openRenameChat,
      onRetry: handleRetry,
      onRowMenuOpenChange: setIsChatListOrderFrozen,
      onSelectChat: handleSelectChat,
      onSendMessage: handleSendMessage,
      onStop: stopGeneration,
      onSubmitFeedback: submitFeedback,
      onToggleChatListColumn: toggleChatListColumn,
      onToggleMode: handleToggleMode,
      onUnhoverChatList: unhoverChatList,
    },
    chatListLoader,
    conversation: {
      displayName,
      feedbackByMessageId,
      isSendPending: isSendPending || isReplySettling,
      messages,
      pendingAnswerFileName,
      status,
      turnStartedAt,
      onLoadOlderMessages: loadOlderMessages,
      onReloadMessages: reloadMessages,
    },
    draft,
    launcher: {
      hasUnread: getHasGretaAIChatUnread(chatStatusById, chats),
      isLocked,
      launcherRef,
      onOpen: handleOpenPanel,
    },
    panel: {
      activeChatId,
      chatAction,
      chatListScrollTopRef,
      chatListSeenChatIdRef,
      chatListWidth,
      chatStatusById,
      isChatListColumnOpen,
      isChatListHovered,
      isChatListOrderFrozen,
      isChatListPinned,
      isLimitReached,
      isLocked,
      isMinimized,
      isOpen,
      mode,
      panelRef,
      view,
    },
    projects,
  }
}
